// src/components/Sidebar.js — responsive, collapsible with memory
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Sidebar.css';
import logo from '../assets/logo.png';

const STORAGE_KEY = 'sidebar_open_v1';

const navItems = [
  { key: 'apps', icon: '🧠', label: '应用广场', to: '/' },
  { key: 'mine', icon: '🏠', label: '我的', to: '/my' },
  { key: 'pay',  icon: '💰', label: '充值', to: '/recharge' },
];

export default function Sidebar(){
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width:1024px)').matches : true));

  // First load: default open (as requested). Persist user choice.
  useEffect(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved === null) {
      setOpen(true); // first time visit -> open
    } else {
      setOpen(saved === '1');
    }
  }, []);

  // Watch media query to know desktop vs mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width:1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener?.('change', onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  // Keep a helper class on <html> for scrim/toggle positioning (optional)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('sidebar-open', open);
    return () => { root.classList.remove('sidebar-open'); };
  }, [open]);

  const containerClass = useMemo(() => {
    // On desktop we don't slide fully out; we allow a 'collapsed' width.
    if (isDesktop) return `sidebar ${open ? '' : 'is-collapsed'}`.trim();
    return `sidebar ${open ? '' : 'is-closed'}`.trim(); // mobile slides off-canvas
  }, [open, isDesktop]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    // optional: if you want to push body margin on desktop
    if (typeof document !== 'undefined' && window.matchMedia('(min-width:1024px)').matches){
      document.body.classList.toggle('is-collapsed', !next);
      document.body.classList.add('with-sidebar');
    }
  };

  return (
    <>
      {/* overlay scrim only on mobile */}
      {!isDesktop && open && <div className="sidebar-scrim" onClick={handleToggle} />}

      <nav className={containerClass} aria-label="主侧边栏">
        <div className="logo-section">
          <img src={logo} alt="本源 AI" className="logo" />
          <h2 className="logo-text">本源 AI</h2>
        </div>

        <ul className="sidebar-menu">
          {navItems.map(item => (
            <li key={item.key} onClick={() => { navigate(item.to); if(!isDesktop) setOpen(false); }}>
              <span className="icon" aria-hidden="true">{item.icon}</span>
              <span className="label">{item.label}</span>
            </li>
          ))}
        </ul>

        <div className="footer">
          <div className="small">© {new Date().getFullYear()} 本源AI</div>
        </div>
      </nav>

      {/* Toggle button */}
      <button
        type="button"
        aria-label={open ? '收起侧边栏' : '展开侧边栏'}
        className="sidebar-toggle"
        onClick={handleToggle}
        title={open ? '收起' : '展开'}
      >
        {open ? '⟨' : '☰'}
      </button>
    </>
  );
}
